"use strict";
/**
 * Copyright © 2019 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.withoutLinkWhenLinkDataEmpty = void 0;

var react_1 = __importDefault(require("react"));

var core_1 = require("@bodiless/core");

var lodash_1 = require("lodash");

var fclasses_1 = require("@bodiless/fclasses");

var NormalHref_1 = __importDefault(require("./NormalHref"));

var DEFAULT_INSTRUCTIONS = "\n  Use a fully formed URL only for external links, e.g., https://www.example.com.\n  Internal links should be specified without a protocol or domain. Internal\n  links beginning with a './' will be relative to the current page. Those not\n  beginning with a './' will be prefixed with '/' and be relative to\n  the site root.  All links will have a trailing slash appended.\n";

var useLinkOverrides = function useLinkOverrides(useOverrides) {
  if (useOverrides === void 0) {
    useOverrides = function useOverrides() {
      return {};
    };
  }

  return function (props) {
    var overrides = useOverrides(props);
    var _a = overrides.submitValueHandler,
        submitValueHandler$ = _a === void 0 ? lodash_1.identity : _a,
        _b = overrides.normalizeHref,
        normalizeHref = _b === void 0 ? function (href) {
      return new NormalHref_1.default(href).toString();
    } : _b,
        _c = overrides.instructions,
        instructions = _c === void 0 ? DEFAULT_INSTRUCTIONS : _c;

    var submitValueHandler = function submitValueHandler(_a) {
      var href = _a.href;
      return submitValueHandler$({
        href: normalizeHref(href)
      });
    };

    var finalOverrides = __assign(__assign({}, overrides), {
      normalizeHref: normalizeHref,
      submitValueHandler: submitValueHandler,
      renderForm: function renderForm(_a) {
        var unwrap = _a.componentProps.unwrap,
            closeForm = _a.closeForm;

        var _b = core_1.useMenuOptionUI(),
            ComponentFormTitle = _b.ComponentFormTitle,
            ComponentFormLabel = _b.ComponentFormLabel,
            ComponentFormText = _b.ComponentFormText,
            ComponentFormUnwrapButton = _b.ComponentFormUnwrapButton,
            ComponentFormDescription = _b.ComponentFormDescription;

        var removeLinkHandler = function removeLinkHandler(event) {
          event.preventDefault();

          if (unwrap) {
            unwrap();
          }

          closeForm(event);
        };

        return react_1.default.createElement(react_1.default.Fragment, null, react_1.default.createElement(ComponentFormTitle, null, "Link"), react_1.default.createElement(ComponentFormLabel, {
          htmlFor: "link-href"
        }, "URL"), react_1.default.createElement(ComponentFormText, {
          field: "href",
          id: "link-href",
          "aria-describedby": "description",
          placeholder: "/link"
        }), react_1.default.createElement(ComponentFormDescription, {
          id: "description"
        }, instructions), unwrap && react_1.default.createElement(ComponentFormUnwrapButton, {
          type: "button",
          onClick: removeLinkHandler
        }, "Remove Link"));
      }
    });

    return finalOverrides;
  };
};

var options = {
  icon: 'link',
  name: 'Link',
  label: 'Edit',
  groupLabel: 'Link',
  groupMerge: 'merge',
  // The actual form is provided by useLinkOverrides above.
  renderForm: function renderForm() {
    return react_1.default.createElement(react_1.default.Fragment, null);
  },
  global: false,
  local: true,
  defaultData: {
    href: ''
  }
};

var withNormalHref = function withNormalHref(useOverrides) {
  return function (Component) {
    var WithNormalHref = function WithNormalHref(_a) {
      var href = _a.href,
          rest = __rest(_a, ["href"]);

      return react_1.default.createElement(Component, __assign({
        href: useOverrides().normalizeHref(href)
      }, rest));
    };

    return WithNormalHref;
  };
};

var asBodilessLink = function asBodilessLink(nodeKeys, defaultData, useOverrides) {
  return lodash_1.flowRight( // Prevent following the link in edit mode
  core_1.ifEditable(core_1.withExtendHandler('onClick', function () {
    return function (e) {
      return e.preventDefault();
    };
  })), core_1.asBodilessComponent(options)(nodeKeys, defaultData, useLinkOverrides(useOverrides)), core_1.withoutProps(['unwrap']), withNormalHref(useLinkOverrides(useOverrides)));
};
/**
 * hook that determines if the link data is empty
 * the hook validates the data in the current node and in the corresponding prop
 *
 * @param props - link based component props
 * @returns true when link data is empty, otherwise false
 */


var useEmptyLinkToggle = function useEmptyLinkToggle(_a) {
  var href = _a.href;
  var node = core_1.useNode().node;
  return (href === undefined || href === '#') && node.data.href === undefined;
};
/**
 * HOC that can be applied to a link based component to not render the component
 * when the component link data is empty
 * Note: the component will still render its children
 *
 * @param Component - link based component
 * @returns Component - Fragment when link data empty, input Component otherwise
 */


var withoutLinkWhenLinkDataEmpty = core_1.ifToggledOn(useEmptyLinkToggle)(fclasses_1.replaceWith(fclasses_1.Fragment));
exports.withoutLinkWhenLinkDataEmpty = withoutLinkWhenLinkDataEmpty;
exports.default = asBodilessLink;